import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useListComponentsQuery } from '../api/authoring'
import type { ManifestReference } from '../api/authoring'
import { LISTING_LAG } from '../model/labels'
import { acceptsLabel } from '../model/references'
import { Loading, QueryError } from './QueryState'

/**
 * Choose what a reference points at.
 *
 * **Linking only, and there is no other tab.** A reference names a
 * component that exists — the surface reads the target's type off it and
 * refuses a stated one — so the inline half of the placement picker has
 * no counterpart here: an inline node has no identity, and an
 * association with nothing to associate is not a thing an author can
 * mean.
 *
 * The type selector is the picker's whole navigation, because the
 * listing takes one type at a time and an edge accepting a role resolves
 * to every type in it. Choosing `custom/practice` first is how an author
 * gets a list they can read instead of every record in the tenant.
 *
 * A **family** edge asks for one more thing: the name. The type declares
 * that the family is named by whoever writes it, and what each name means
 * comes from the pattern declaration this placement runs — so the field
 * is free text with the declaration's own names as the guidance, not a
 * vocabulary this client can offer.
 */
export function ReferencePicker({
  tenant,
  declares,
  open,
  onClose,
  onChoose,
  busy,
}: {
  tenant: string
  /** The edge being filled, or undefined when the dialog is closed. */
  declares: ManifestReference | undefined
  open: boolean
  onClose: () => void
  /** The chosen target, its type, and the name it plays under. */
  onChoose: (target: { id: string; type: string; name: string }) => void
  busy: boolean
}) {
  const accepts = declares?.accepts ?? []
  const family = Boolean(declares?.authoredNames)

  const [type, setType] = useState(accepts[0] ?? '')
  const [name, setName] = useState('')

  // Reset when the dialog opens, for the reason the placement picker
  // does: the controls otherwise keep whatever the last edge left them
  // on, and an author filling `input` would find the name they typed for
  // a binding still in the box.
  useEffect(() => {
    if (open) {
      setType(accepts[0] ?? '')
      setName('')
    }
    // The accepted list is stable for one edge; depending on it directly
    // would reset the controls on every render of the holding screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, declares?.name])

  const listing = useListComponentsQuery({ tenant, type, limit: 100 }, {
    skip: !open || !type,
  })
  const components = listing.data?.components ?? []
  const named = family ? name.trim() : (declares?.name ?? '')

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle>
        {family ? 'Add supporting content' : <>Choose {referenceLabel(declares?.name)}</>}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant='body2' color='text.secondary'>
            {declares ? `Choose ${acceptsLabel(declares)}.` : ''}
          </Typography>

          {family && (
            <TextField
              label='Name'
              value={name}
              onChange={(event) => setName(event.target.value)}
              helperText='The name the pattern declaration reads this record under — its bindings spell it, as in $business.tagline.'
            />
          )}

          <TextField
            select
            label='Type'
            value={type}
            onChange={(event) => setType(event.target.value)}
            sx={{ maxWidth: 280 }}
            disabled={accepts.length === 0}
            helperText='The compatible kinds of content.'
          >
            {accepts.map((accepted: string) => (
              <MenuItem key={accepted} value={accepted}>
                {accepted}
              </MenuItem>
            ))}
          </TextField>

          {accepts.length === 0 && (
            <Alert severity='info'>
              Nothing in this tenant is of a type this edge accepts. Declare one first — an edge accepting a role lists
              every type in that role, so an empty list means the tenant has none.
            </Alert>
          )}

          {listing.isLoading && <Loading label='Listing components…' />}
          {listing.error && (
            <QueryError
              error={listing.error}
              title='Could not list components'
            />
          )}

          {!listing.isLoading && !listing.error && type &&
            components.length === 0 && <Alert severity='info'>No {type} components in this tenant.</Alert>}

          {components.length > 0 && (
            <List
              dense
              disablePadding
              sx={{ maxHeight: 300, overflowY: 'auto' }}
            >
              {components.map((component) => (
                <ListItemButton
                  key={component.id}
                  disabled={busy || (family && !named)}
                  onClick={() =>
                    onChoose({
                      id: component.id,
                      type: component.type,
                      name: named,
                    })}
                >
                  <ListItemText
                    primary={component.label ?? component.id}
                    secondary={component.id}
                  />
                </ListItemButton>
              ))}
            </List>
          )}

          <Typography variant='caption' color='text.secondary'>
            {LISTING_LAG}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  )
}

function referenceLabel(name: string | undefined): string {
  switch (name) {
    case 'declaration':
      return 'how it looks'
    case 'input':
      return 'content'
    case 'source':
      return 'a collection'
    default:
      return name ?? ''
  }
}
